import { z } from 'zod'
import { Channel } from './enums'

/**
 * The AI brain's console contract (design 8, 9; screens 18.7 Classification and 18.10 Playbooks): customer
 * memory, knowledge sources and gaps, labels and threshold suggestions, AI budgets and spending, and the
 * platform and pack library.
 */

// ---------------------------------------------------------------------------
// Customer memory (design 8.5)
// ---------------------------------------------------------------------------
export interface MemoryFactView {
  id: string
  fact: string
  /** record | staff | customer | ai: what a customer says is less certain than records or staff. */
  source_kind: string
  confidence: number
  status: 'active' | 'forgotten'
  expires_at: string | null
  expired: boolean
  created_at: string
  source_message_id: string | null
  conversation_id: string | null
  created_by: string | null
}
export interface MemoryView { summary: string | null; summary_at: string | null; facts: MemoryFactView[]; forgotten: number }

export const MemoryFactInput = z.object({ fact: z.string().trim().min(3).max(240), expires_at: z.string().datetime({ offset: true }).nullable().optional() })
export const MemoryFactPatch = z.object({ fact: z.string().trim().min(3).max(240).optional(), expires_at: z.string().datetime({ offset: true }).nullable().optional() })
export const MemoryForget = z.object({ reason: z.string().max(500).optional() })
export const MemorySummaryInput = z.object({ summary: z.string().max(1500).nullable() })

// ---------------------------------------------------------------------------
// Knowledge (design 8.3)
// ---------------------------------------------------------------------------
export interface KnowledgeArticleDetail {
  id: string; title: string; body: string; status: string; version: number
  review_at: string | null; updated_at: string
  scope: { sites?: string[]; channels?: Channel[] }
  owner_id: string | null; owner_name: string | null
  source_id: string | null; source_url: string | null; fetched_at: string | null
  /** The review date has passed. */
  review_due: boolean
  chunks: number
}

export const KnowledgeSourceInput = z.object({
  name: z.string().trim().min(1).max(200),
  url: z.string().url().refine((u) => /^https?:\/\//i.test(u), 'Use an http or https address'),
  /** How many pages to read at most, and the path the crawl stays under (default: the start URL's folder). */
  max_pages: z.number().int().min(1).max(200).default(25),
  path_prefix: z.string().max(300).nullish(),
  scope: z.object({ sites: z.array(z.string()).optional(), channels: z.array(Channel).optional() }).default({}),
  owner_id: z.string().uuid().nullish(),
  review_days: z.number().int().min(1).max(730).default(90),
  /** Re-read the site this often; null for manual syncs only. */
  sync_hours: z.number().int().min(1).max(24 * 30).nullable().default(24),
})
export type KnowledgeSourceInput = z.infer<typeof KnowledgeSourceInput>
export const KnowledgeSourcePatch = KnowledgeSourceInput.partial().extend({ status: z.enum(['active', 'paused']).optional() })

/** A document to turn into articles: PDF, Word (.docx), Markdown or plain text, base64-encoded. At most 8 MB. */
export const KnowledgeFileInput = z.object({
  name: z.string().trim().min(1).max(200),
  content_base64: z.string().min(1).max(11_200_000),
  mime: z.string().max(200).optional(),
  scope: z.object({ sites: z.array(z.string()).optional(), channels: z.array(Channel).optional() }).default({}),
  owner_id: z.string().uuid().nullish(),
  review_days: z.number().int().min(1).max(730).default(180),
})
export type KnowledgeFileInput = z.infer<typeof KnowledgeFileInput>

export interface KnowledgeSourceView {
  id: string; kind: 'url' | 'file'; name: string; url: string | null; file_name: string | null
  max_pages: number; path_prefix: string | null
  scope: { sites?: string[]; channels?: Channel[] }
  owner_id: string | null; review_days: number; sync_hours: number | null; status: 'active' | 'paused'
  last_synced_at: string | null; next_sync_at: string | null
  /** ok: nothing changed; changed: a change set was drafted; failed; blocked (robots.txt or no pages) */
  last_status: string | null; last_error: string | null; failures: number
  pages: number; articles: number
  last_change_set: { id: string; ref: string; status: string } | null
}

export interface KnowledgeSyncResult {
  source: KnowledgeSourceView
  outcome: 'changed' | 'unchanged' | 'failed' | 'blocked'
  detail: string
  /** The drafted change set with the new, changed and removed articles (test, approve and release it). */
  change_set_id: string | null
  pages_read: number
  skipped: { url: string; why: string }[]
}

export interface KnowledgeGapView {
  id: string; question: string; created_at: string; status: 'open' | 'resolved' | 'dismissed'
  conversation_id: string | null; conversation_ref: string | null; channel: string | null
  /** Other open gaps that ask much the same thing. */
  similar: number
  article_id: string | null; note: string | null
}
export const KnowledgeGapAction = z.discriminatedUnion('action', [
  z.object({ action: z.literal('resolve'), article_id: z.string().uuid().optional(), note: z.string().max(500).optional() }),
  z.object({ action: z.literal('dismiss'), note: z.string().max(500).optional() }),
  z.object({ action: z.literal('reopen') }),
])

// ---------------------------------------------------------------------------
// Classification: labels, recent decisions, threshold suggestions (design 9.3, 9.4)
// ---------------------------------------------------------------------------
export interface DecisionLabelView { correct: boolean; source: string; kind: 'implicit' | 'review'; by: string | null; at: string; note: string | null }
export interface DecisionRowView {
  id: string; created_at: string; question_key: string
  message_id: string | null; conversation_id: string | null; conversation_ref: string | null
  text: string | null; channel: string | null
  decided_by: 'rule' | 'jev' | 'llm' | 'none'; answer: string | null; probability: number | null; fired: boolean
  fallback_reason: string | null; input_note: string | null; latency_ms: number | null
  label: DecisionLabelView | null
  /** What made it (G 16.6-08): the model id the vendor reported (with its version) and the vendor; for a rule, its version. */
  model?: string | null; provider?: string | null; rule_version?: string | null
}
export const DecisionLabelInput = z.object({ correct: z.boolean(), note: z.string().max(500).optional() })

export interface ThresholdSuggestionView {
  ok: boolean; reason: string; threshold: number | null
  current: { threshold: number; precision: number | null; recall: number | null }
  suggested: { precision: number | null; recall: number | null } | null
  samples: number; positives: number
}

export interface QuestionInsightView {
  key: string
  recent: DecisionRowView[]
  /** Labels from the desk and reviews in the last 7 days. */
  labels_this_week: number
  /** From labels (reviews count first): of the times it fired, how often right; of the real cases, how many it caught. */
  accuracy: { precision: number | null; recall: number | null; labels: number; reviewed: number; tp: number; fp: number; fn: number; tn: number }
  by_channel: { channels: string; decisions: number; fired: number; precision: number | null }[]
  /** All questions together, per 1,000 messages classified (last 30 days). */
  cost_per_1000_messages_micros: number | null
  latency_p95_ms: { jev: number | null; llm: number | null }
  suggestion: ThresholdSuggestionView
}

/** Decisions waiting for a person to say whether they were right: unlabelled, sampled across fired and not fired. */
export interface LabelQueueView { items: DecisionRowView[]; fired: number; not_fired: number }

// ---------------------------------------------------------------------------
// AI spending and health (design 8.2, 8.7, 9.4)
// ---------------------------------------------------------------------------
export const BudgetInput = z.object({
  /** US dollars a month; null removes the budget. */
  limit_usd: z.number().positive().max(1_000_000).nullable(),
  soft_ratio: z.number().min(0.5).max(0.99).default(0.9),
})
export interface AiSpendView {
  month: string
  budget: { limit_micros: number | null; soft_ratio: number; used_micros: number; state: 'ok' | 'soft' | 'exhausted' | 'none'; updated_by: string | null }
  by_purpose: { purpose: string; calls: number; failures: number; cost_micros: number; cached_tokens: number; input_tokens: number; p95_ms: number | null; budget_ms: number | null }[]
  /** `own`: this company's route, released after a model comparison (C 8.2-02); otherwise the deployment's. */
  routes: { purpose: string; models: string[]; timeout_ms: number; own?: boolean }[]
  calibration: { samples: number; updated_at: string | null; bins: { lo: number; hi: number; n: number; good: number }[]; by_request_type: { request_type: string; samples: number }[] }
  allowed_promises: { kind: string; max_hours?: number; needs_action?: string }[]
  alarms: { kind: string; subject: string; message: string; severity: string; opened_at: string }[]
}
export const AllowedPromisesInput = z.object({
  allowed_promises: z.array(z.object({
    kind: z.enum(['callback', 'send_item', 'refund', 'follow_up', 'other']),
    max_hours: z.number().positive().max(24 * 90).optional(),
    needs_action: z.string().optional(),
  })).max(10),
})

// ---------------------------------------------------------------------------
// Library layers (design 8.4, 9.1): platform and industry-pack playbooks and questions
// ---------------------------------------------------------------------------
export interface LibraryItemView {
  entity: 'decision_question' | 'playbook'
  key: string; name: string; layer: 'platform' | 'pack'; pack: string | null; pack_name: string | null
  goal: string | null
  /** missing: not in the company yet; current: same as the library; customised: changed by the company; update_available: the library changed. */
  status: 'missing' | 'current' | 'customised' | 'update_available' | 'merged' | 'applied' | 'dismissed'
  change_set_id: string | null
}
export interface LibrarySyncResult { applied: number; merged: number; proposed: number; change_set_ids: string[] }

export interface PlaybookTestView { passed: number; failed: number; cases: { name: string; ok: boolean; detail: string }[]; ran_at: string }
